import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Pill } from '../components/ui/Pill'
import { Toggle } from '../components/ui/Toggle'
import { Icon, I } from '../components/ui/icons'
import { listChannelConnections, listChannelStoreMethods, refreshChannelStoreMethods } from '../channels/channelsApi'
import type { ChannelConnection, ChannelProvider, ChannelStoreMethod, ChannelStoreMethodRefreshCategory } from '../channels/types'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import {
  createZohoOrderPolicy,
  getActiveZohoOrderPolicy,
  listZohoMasterData,
  listZohoOrderPolicies,
  refreshZohoMasterData,
  setZohoOrderPolicyAutoSend,
  updateZohoOrderPolicy,
} from '../inventory/inventoryApi'
import type {
  ChannelOrderStatus,
  ZohoMasterDataCategory,
  ZohoMasterDataItem,
  ZohoInvoiceSourceMode,
  ZohoOrderLifecycleAction,
  ZohoOrderPolicy,
  ZohoOrderPolicySaveRequest,
  ZohoOrderPolicySkuMatchOperator,
  ZohoOrderPolicySkuMatchScope,
  ZohoOrderPolicyWarehouseOverrideRuleRequest,
} from '../inventory/types'
import { listProducts } from '../products/productsApi'
import type { ProductListItem } from '../products/types'

const MASTER_DATA_CATEGORIES: ZohoMasterDataCategory[] = [
  'CUSTOMER',
  'WAREHOUSE',
  'SALES_PERSON',
  'PAYMENT_TERMS',
  'DELIVERY_METHOD',
  'REPORTING_TAG',
  'REPORTING_TAG_OPTION',
]

const REFRESH_MASTER_DATA_CATEGORIES: ZohoMasterDataCategory[] = [
  'CUSTOMER',
  'WAREHOUSE',
  'SALES_PERSON',
  'PAYMENT_TERMS',
  'DELIVERY_METHOD',
  'REPORTING_TAG',
]

const ORDER_STATUSES: ChannelOrderStatus[] = ['NEW', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED', 'UNKNOWN']
const LIFECYCLE_ACTIONS: ZohoOrderLifecycleAction[] = ['CREATE_SALES_ORDER', 'CONFIRM_SALES_ORDER', 'CREATE_INVOICE', 'VOID_SALES_ORDER', 'CREATE_CREDIT_NOTE']
const INVOICE_SOURCE_MODES: ZohoInvoiceSourceMode[] = ['LOCAL_ORDER', 'ZOHO_SALES_ORDER_SNAPSHOT']

type MasterDataState = Record<ZohoMasterDataCategory, ZohoMasterDataItem[]>

type PaymentCriteriaField =
  | 'channelScope'
  | 'channelPaymentMethod'
  | 'shippingCondition'
  | 'paymentMethodName'
  | 'paymentMethodCode'
  | 'paymentMethodType'
  | 'paymentMethodExternalId'
  | 'paymentStatus'
  | 'shippingMethodExternalId'
  | 'shippingMethodCode'
  | 'shippingCompany'
  | 'shippingMethod'

type ShippingCriteriaField =
  | 'channelScope'
  | 'channelDeliveryOption'
  | 'shippingMethodExternalId'
  | 'shippingMethodCode'
  | 'shippingCompany'
  | 'shippingMethod'

const PAYMENT_CRITERIA_FIELD_ORDER: PaymentCriteriaField[] = [
  'channelPaymentMethod',
  'shippingCondition',
  'channelScope',
  'paymentMethodExternalId',
  'paymentMethodCode',
  'paymentMethodName',
  'paymentMethodType',
  'paymentStatus',
  'shippingMethodExternalId',
  'shippingMethodCode',
  'shippingCompany',
  'shippingMethod',
]

const SHIPPING_CRITERIA_FIELD_ORDER: ShippingCriteriaField[] = [
  'channelDeliveryOption',
  'channelScope',
  'shippingMethodExternalId',
  'shippingMethodCode',
  'shippingCompany',
  'shippingMethod',
]

interface PaymentMappingDraft {
  criteriaFields: PaymentCriteriaField[]
  channelProvider: string
  channelConnectionId: string
  selectedPaymentStoreMethodId: string
  selectedShippingStoreMethodId: string
  paymentMethodName: string
  paymentMethodCode: string
  paymentMethodType: string
  paymentMethodExternalId: string
  paymentStatus: string
  shippingMethodExternalId: string
  shippingMethodCode: string
  shippingCompany: string
  shippingMethod: string
  paymentTermsExternalId: string
  paymentFeeServiceItemProductId: string
  paymentFeeServiceItemLabel: string
}

interface ShippingMappingDraft {
  criteriaFields: ShippingCriteriaField[]
  channelProvider: string
  channelConnectionId: string
  selectedShippingStoreMethodId: string
  shippingMethodExternalId: string
  shippingMethodCode: string
  shippingCompany: string
  shippingMethod: string
  deliveryMethodExternalId: string
  shippingFeeServiceItemProductId: string
  shippingFeeServiceItemLabel: string
}

interface ActionMappingDraft {
  priority: string
  action: ZohoOrderLifecycleAction
  orderStatuses: ChannelOrderStatus[]
  rawStatuses: string
}

interface WarehouseOverrideDraft {
  priority: string
  channelProvider: string
  channelConnectionId: string
  orderStatus: string
  rawStatus: string
  paymentStatus: string
  paymentMethodCode: string
  shippingCompany: string
  shippingMethod: string
  fulfillmentChannel: string
  fulfillmentStatus: string
  skuMatchScope: ZohoOrderPolicySkuMatchScope | ''
  skuMatchOperator: ZohoOrderPolicySkuMatchOperator | ''
  skuMatchValue: string
  warehouseExternalId: string
}

interface SyncRuleWizardPageProps {
  accessToken: string
  connectionId: number | null
  policyId?: number | null
  onBack?: () => void
}

const DEFAULT_ACTION_MAPPINGS: ActionMappingDraft[] = [
  { priority: '10', action: 'CREATE_SALES_ORDER', orderStatuses: ['NEW'], rawStatuses: '' },
  { priority: '20', action: 'CONFIRM_SALES_ORDER', orderStatuses: ['IN_PROGRESS'], rawStatuses: '' },
  { priority: '30', action: 'CREATE_INVOICE', orderStatuses: ['COMPLETED'], rawStatuses: '' },
  { priority: '40', action: 'VOID_SALES_ORDER', orderStatuses: ['CANCELLED'], rawStatuses: '' },
]

const EMPTY_PAYMENT_MAPPING: PaymentMappingDraft = {
  criteriaFields: [],
  channelProvider: '',
  channelConnectionId: '',
  selectedPaymentStoreMethodId: '',
  selectedShippingStoreMethodId: '',
  paymentMethodName: '',
  paymentMethodCode: '',
  paymentMethodType: '',
  paymentMethodExternalId: '',
  paymentStatus: '',
  shippingMethodExternalId: '',
  shippingMethodCode: '',
  shippingCompany: '',
  shippingMethod: '',
  paymentTermsExternalId: '',
  paymentFeeServiceItemProductId: '',
  paymentFeeServiceItemLabel: '',
}

const EMPTY_SHIPPING_MAPPING: ShippingMappingDraft = {
  criteriaFields: [],
  channelProvider: '',
  channelConnectionId: '',
  selectedShippingStoreMethodId: '',
  shippingMethodExternalId: '',
  shippingMethodCode: '',
  shippingCompany: '',
  shippingMethod: '',
  deliveryMethodExternalId: '',
  shippingFeeServiceItemProductId: '',
  shippingFeeServiceItemLabel: '',
}

const EMPTY_WAREHOUSE_OVERRIDE: WarehouseOverrideDraft = {
  priority: '100',
  channelProvider: '',
  channelConnectionId: '',
  orderStatus: '',
  rawStatus: '',
  paymentStatus: '',
  paymentMethodCode: '',
  shippingCompany: '',
  shippingMethod: '',
  fulfillmentChannel: '',
  fulfillmentStatus: '',
  skuMatchScope: '',
  skuMatchOperator: '',
  skuMatchValue: '',
  warehouseExternalId: '',
}

function emptyMasterData(): MasterDataState {
  return {
    ORGANIZATION: [],
    CUSTOMER: [],
    WAREHOUSE: [],
    SALES_PERSON: [],
    PAYMENT_TERMS: [],
    DELIVERY_METHOD: [],
    REPORTING_TAG: [],
    REPORTING_TAG_OPTION: [],
  }
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `pages.syncRuleWizard.errors.codes.${error.code}`
  }
  return 'pages.syncRuleWizard.errors.codes.REQUEST_FAILED'
}

function fieldStyle() {
  return {
    width: '100%',
    minWidth: 0,
    border: '1px solid var(--border-default)',
    borderRadius: 10,
    padding: '9px 10px',
    fontFamily: 'inherit',
    fontSize: 13,
    color: 'var(--fg)',
    background: 'white',
    boxSizing: 'border-box' as const,
    maxWidth: '100%',
  }
}

function TextInput({ label, value, onChange, placeholder, disabled = false }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string; disabled?: boolean }) {
  return (
    <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>{label}</span>
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} disabled={disabled} style={fieldStyle()} />
    </label>
  )
}

function SelectField({ label, value, onChange, items, placeholder, disabled = false }: { label: string; value: string; onChange: (value: string) => void; items: ZohoMasterDataItem[]; placeholder: string; disabled?: boolean }) {
  return (
    <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} disabled={disabled} style={fieldStyle()}>
        <option value="">{placeholder}</option>
        {items.map(item => (
          <option key={item.externalId} value={item.externalId}>{item.displayName}</option>
        ))}
      </select>
    </label>
  )
}

function OptionSelect({ label, value, onChange, options, placeholder, disabled = false }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }>; placeholder: string; disabled?: boolean }) {
  return (
    <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} disabled={disabled} style={fieldStyle()}>
        <option value="">{placeholder}</option>
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  )
}

function CheckboxRow({ label, checked, onChange, disabled = false }: { label: string; checked: boolean; onChange: (checked: boolean) => void; disabled?: boolean }) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'white', minWidth: 0 }}>
      <input type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} disabled={disabled} style={{ accentColor: 'var(--brand-500)' }} />
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', minWidth: 0, overflowWrap: 'break-word' }}>{label}</span>
    </label>
  )
}

function optional(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

function parseDelimitedValues(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean)
}

function toggleValue<T extends string>(values: T[], value: T, checked: boolean): T[] {
  if (checked) return values.includes(value) ? values : [...values, value]
  return values.filter(item => item !== value)
}

function hasWarehouseOverrideCondition(rule: WarehouseOverrideDraft) {
  return Boolean(
    optional(rule.channelProvider) ||
    optional(rule.channelConnectionId) ||
    optional(rule.orderStatus) ||
    optional(rule.rawStatus) ||
    optional(rule.paymentStatus) ||
    optional(rule.paymentMethodCode) ||
    optional(rule.shippingCompany) ||
    optional(rule.shippingMethod) ||
    optional(rule.fulfillmentChannel) ||
    optional(rule.fulfillmentStatus) ||
    optional(rule.skuMatchValue)
  )
}

function isBlankCriteria(value?: string | null): boolean {
  return value == null || value.trim() === ''
}

function hasPaymentMappingCriteria(mapping: PaymentMappingDraft): boolean {
  return Boolean(
    optional(mapping.channelProvider) ||
    optional(mapping.channelConnectionId) ||
    optional(mapping.paymentMethodExternalId) ||
    optional(mapping.paymentMethodCode) ||
    optional(mapping.paymentMethodName) ||
    optional(mapping.paymentMethodType) ||
    optional(mapping.paymentStatus) ||
    optional(mapping.shippingMethodExternalId) ||
    optional(mapping.shippingMethodCode) ||
    optional(mapping.shippingCompany) ||
    optional(mapping.shippingMethod)
  )
}

function hasShippingMappingCriteria(mapping: ShippingMappingDraft): boolean {
  return Boolean(
    optional(mapping.channelProvider) ||
    optional(mapping.channelConnectionId) ||
    optional(mapping.shippingMethodExternalId) ||
    optional(mapping.shippingMethodCode) ||
    optional(mapping.shippingCompany) ||
    optional(mapping.shippingMethod)
  )
}

function uniqueCriteriaFields<T extends string>(fields: T[]): T[] {
  return Array.from(new Set(fields))
}

function paymentCriteriaFieldsFor(mapping: PaymentMappingDraft): PaymentCriteriaField[] {
  if (mapping.criteriaFields.length > 0) return mapping.criteriaFields
  const fields: PaymentCriteriaField[] = []
  if (optional(mapping.channelConnectionId)) fields.push('channelScope')
  if (optional(mapping.selectedPaymentStoreMethodId)) fields.push('channelPaymentMethod')
  if (optional(mapping.selectedShippingStoreMethodId)) fields.push('shippingCondition')
  if (optional(mapping.paymentMethodExternalId)) fields.push('paymentMethodExternalId')
  if (optional(mapping.paymentMethodCode)) fields.push('paymentMethodCode')
  if (optional(mapping.paymentMethodName)) fields.push('paymentMethodName')
  if (optional(mapping.paymentMethodType)) fields.push('paymentMethodType')
  if (optional(mapping.paymentStatus)) fields.push('paymentStatus')
  if (optional(mapping.shippingMethodExternalId)) fields.push('shippingMethodExternalId')
  if (optional(mapping.shippingMethodCode)) fields.push('shippingMethodCode')
  if (optional(mapping.shippingCompany)) fields.push('shippingCompany')
  if (optional(mapping.shippingMethod)) fields.push('shippingMethod')
  return fields
}

function shippingCriteriaFieldsFor(mapping: ShippingMappingDraft): ShippingCriteriaField[] {
  if (mapping.criteriaFields.length > 0) return mapping.criteriaFields
  const fields: ShippingCriteriaField[] = []
  if (optional(mapping.channelConnectionId)) fields.push('channelScope')
  if (optional(mapping.selectedShippingStoreMethodId)) fields.push('channelDeliveryOption')
  if (optional(mapping.shippingMethodExternalId)) fields.push('shippingMethodExternalId')
  if (optional(mapping.shippingMethodCode)) fields.push('shippingMethodCode')
  if (optional(mapping.shippingCompany)) fields.push('shippingCompany')
  if (optional(mapping.shippingMethod)) fields.push('shippingMethod')
  return fields
}

function clearPaymentCriteriaField(mapping: PaymentMappingDraft, field: PaymentCriteriaField): PaymentMappingDraft {
  const remaining = paymentCriteriaFieldsFor(mapping).filter(candidate => candidate !== field)
  const next: PaymentMappingDraft = { ...mapping, criteriaFields: remaining }
  if (field === 'channelScope') {
    const keepsScopedStoreCondition = remaining.includes('channelPaymentMethod') || remaining.includes('shippingCondition')
    return keepsScopedStoreCondition ? next : { ...next, channelProvider: '', channelConnectionId: '' }
  }
  if (field === 'channelPaymentMethod') {
    return {
      ...next,
      selectedPaymentStoreMethodId: '',
      paymentMethodName: remaining.includes('paymentMethodName') ? next.paymentMethodName : '',
      paymentMethodCode: remaining.includes('paymentMethodCode') ? next.paymentMethodCode : '',
      paymentMethodType: remaining.includes('paymentMethodType') ? next.paymentMethodType : '',
      paymentMethodExternalId: remaining.includes('paymentMethodExternalId') ? next.paymentMethodExternalId : '',
      channelProvider: remaining.includes('channelScope') || remaining.includes('shippingCondition') ? next.channelProvider : '',
      channelConnectionId: remaining.includes('channelScope') || remaining.includes('shippingCondition') ? next.channelConnectionId : '',
    }
  }
  if (field === 'shippingCondition') {
    return {
      ...next,
      selectedShippingStoreMethodId: '',
      shippingMethodExternalId: remaining.includes('shippingMethodExternalId') ? next.shippingMethodExternalId : '',
      shippingMethodCode: remaining.includes('shippingMethodCode') ? next.shippingMethodCode : '',
      shippingMethod: remaining.includes('shippingMethod') ? next.shippingMethod : '',
      channelProvider: remaining.includes('channelScope') || remaining.includes('channelPaymentMethod') ? next.channelProvider : '',
      channelConnectionId: remaining.includes('channelScope') || remaining.includes('channelPaymentMethod') ? next.channelConnectionId : '',
    }
  }
  return { ...next, [field]: '' }
}

function clearShippingCriteriaField(mapping: ShippingMappingDraft, field: ShippingCriteriaField): ShippingMappingDraft {
  const remaining = shippingCriteriaFieldsFor(mapping).filter(candidate => candidate !== field)
  const next: ShippingMappingDraft = { ...mapping, criteriaFields: remaining }
  if (field === 'channelScope') {
    return remaining.includes('channelDeliveryOption') ? next : { ...next, channelProvider: '', channelConnectionId: '' }
  }
  if (field === 'channelDeliveryOption') {
    return {
      ...next,
      selectedShippingStoreMethodId: '',
      shippingMethodExternalId: remaining.includes('shippingMethodExternalId') ? next.shippingMethodExternalId : '',
      shippingMethodCode: remaining.includes('shippingMethodCode') ? next.shippingMethodCode : '',
      shippingMethod: remaining.includes('shippingMethod') ? next.shippingMethod : '',
      channelProvider: remaining.includes('channelScope') ? next.channelProvider : '',
      channelConnectionId: remaining.includes('channelScope') ? next.channelConnectionId : '',
    }
  }
  return { ...next, [field]: '' }
}

function hydratePaymentCriteriaFields(mapping: PaymentMappingDraft): PaymentMappingDraft {
  return { ...mapping, criteriaFields: paymentCriteriaFieldsFor(mapping) }
}

function hydrateShippingCriteriaFields(mapping: ShippingMappingDraft): ShippingMappingDraft {
  return { ...mapping, criteriaFields: shippingCriteriaFieldsFor(mapping) }
}

function clearPaymentStoreMethodValues(mapping: PaymentMappingDraft): Partial<PaymentMappingDraft> {
  const activeFields = paymentCriteriaFieldsFor(mapping)
  return {
    selectedPaymentStoreMethodId: '',
    paymentMethodName: activeFields.includes('paymentMethodName') ? mapping.paymentMethodName : '',
    paymentMethodCode: activeFields.includes('paymentMethodCode') ? mapping.paymentMethodCode : '',
    paymentMethodType: activeFields.includes('paymentMethodType') ? mapping.paymentMethodType : '',
    paymentMethodExternalId: activeFields.includes('paymentMethodExternalId') ? mapping.paymentMethodExternalId : '',
  }
}

function clearPaymentShippingConditionValues(mapping: PaymentMappingDraft): Partial<PaymentMappingDraft> {
  const activeFields = paymentCriteriaFieldsFor(mapping)
  return {
    selectedShippingStoreMethodId: '',
    shippingMethodExternalId: activeFields.includes('shippingMethodExternalId') ? mapping.shippingMethodExternalId : '',
    shippingMethodCode: activeFields.includes('shippingMethodCode') ? mapping.shippingMethodCode : '',
    shippingMethod: activeFields.includes('shippingMethod') ? mapping.shippingMethod : '',
  }
}

function clearShippingStoreMethodValues(mapping: ShippingMappingDraft): Partial<ShippingMappingDraft> {
  const activeFields = shippingCriteriaFieldsFor(mapping)
  return {
    selectedShippingStoreMethodId: '',
    shippingMethodExternalId: activeFields.includes('shippingMethodExternalId') ? mapping.shippingMethodExternalId : '',
    shippingMethodCode: activeFields.includes('shippingMethodCode') ? mapping.shippingMethodCode : '',
    shippingMethod: activeFields.includes('shippingMethod') ? mapping.shippingMethod : '',
  }
}

function paymentCriteriaPayload(mapping: PaymentMappingDraft) {
  const activeFields = paymentCriteriaFieldsFor(mapping)
  const includesChannelScope = activeFields.includes('channelScope') || activeFields.includes('channelPaymentMethod') || activeFields.includes('shippingCondition')
  const includesPaymentStoreMethod = activeFields.includes('channelPaymentMethod')
  const includesShippingCondition = activeFields.includes('shippingCondition')
  const channelConnectionId = includesChannelScope ? optional(mapping.channelConnectionId) : null
  return {
    channelProvider: includesChannelScope ? optional(mapping.channelProvider) : null,
    channelConnectionId: channelConnectionId == null ? null : Number(channelConnectionId),
    paymentMethodName: activeFields.includes('paymentMethodName') || includesPaymentStoreMethod ? optional(mapping.paymentMethodName) : null,
    paymentStatus: activeFields.includes('paymentStatus') ? optional(mapping.paymentStatus) : null,
    paymentMethodCode: activeFields.includes('paymentMethodCode') || includesPaymentStoreMethod ? optional(mapping.paymentMethodCode) : null,
    paymentMethodType: activeFields.includes('paymentMethodType') || includesPaymentStoreMethod ? optional(mapping.paymentMethodType) : null,
    paymentMethodExternalId: activeFields.includes('paymentMethodExternalId') || includesPaymentStoreMethod ? optional(mapping.paymentMethodExternalId) : null,
    shippingMethodExternalId: activeFields.includes('shippingMethodExternalId') || includesShippingCondition ? optional(mapping.shippingMethodExternalId) : null,
    shippingMethodCode: activeFields.includes('shippingMethodCode') || includesShippingCondition ? optional(mapping.shippingMethodCode) : null,
    shippingCompany: activeFields.includes('shippingCompany') ? optional(mapping.shippingCompany) : null,
    shippingMethod: activeFields.includes('shippingMethod') || includesShippingCondition ? optional(mapping.shippingMethod) : null,
  }
}

function shippingCriteriaPayload(mapping: ShippingMappingDraft) {
  const activeFields = shippingCriteriaFieldsFor(mapping)
  const includesChannelScope = activeFields.includes('channelScope') || activeFields.includes('channelDeliveryOption')
  const includesDeliveryOption = activeFields.includes('channelDeliveryOption')
  const channelConnectionId = includesChannelScope ? optional(mapping.channelConnectionId) : null
  return {
    channelProvider: includesChannelScope ? optional(mapping.channelProvider) : null,
    channelConnectionId: channelConnectionId == null ? null : Number(channelConnectionId),
    shippingMethodExternalId: activeFields.includes('shippingMethodExternalId') || includesDeliveryOption ? optional(mapping.shippingMethodExternalId) : null,
    shippingMethodCode: activeFields.includes('shippingMethodCode') || includesDeliveryOption ? optional(mapping.shippingMethodCode) : null,
    shippingCompany: activeFields.includes('shippingCompany') ? optional(mapping.shippingCompany) : null,
    shippingMethod: activeFields.includes('shippingMethod') || includesDeliveryOption ? optional(mapping.shippingMethod) : null,
  }
}

function isDefaultPaymentMapping(mapping: {
  channelProvider?: string | null
  channelConnectionId?: number | null
  paymentMethodCode?: string | null
  paymentMethodName?: string | null
  paymentMethodType?: string | null
  paymentMethodExternalId?: string | null
  paymentStatus?: string | null
  shippingMethodExternalId?: string | null
  shippingMethodCode?: string | null
  shippingCompany?: string | null
  shippingMethod?: string | null
}): boolean {
  return (
    isBlankCriteria(mapping.channelProvider) &&
    mapping.channelConnectionId == null &&
    isBlankCriteria(mapping.paymentMethodCode) &&
    isBlankCriteria(mapping.paymentMethodName) &&
    isBlankCriteria(mapping.paymentMethodType) &&
    isBlankCriteria(mapping.paymentMethodExternalId) &&
    isBlankCriteria(mapping.paymentStatus) &&
    isBlankCriteria(mapping.shippingMethodExternalId) &&
    isBlankCriteria(mapping.shippingMethodCode) &&
    isBlankCriteria(mapping.shippingCompany) &&
    isBlankCriteria(mapping.shippingMethod)
  )
}

function isDefaultShippingMapping(mapping: {
  channelProvider?: string | null
  channelConnectionId?: number | null
  shippingMethodExternalId?: string | null
  shippingMethodCode?: string | null
  shippingCompany?: string | null
  shippingMethod?: string | null
}): boolean {
  return (
    isBlankCriteria(mapping.channelProvider) &&
    mapping.channelConnectionId == null &&
    isBlankCriteria(mapping.shippingMethodExternalId) &&
    isBlankCriteria(mapping.shippingMethodCode) &&
    isBlankCriteria(mapping.shippingCompany) &&
    isBlankCriteria(mapping.shippingMethod)
  )
}

function channelProviderForId(connections: ChannelConnection[], channelConnectionId: string): ChannelProvider | '' {
  const id = Number(optional(channelConnectionId))
  return connections.find(connection => connection.id === id)?.provider ?? ''
}

function storeMethodLabel(method: ChannelStoreMethod, connection?: ChannelConnection): string {
  const scope = connection ? `${connection.displayName} · ` : ''
  const details = [method.code, method.subtype].filter(Boolean).join(' · ')
  return `${scope}${method.displayName}${details ? ` (${details})` : ''}`
}

function productServiceItemLabel(product: Pick<ProductListItem, 'sku' | 'name'>): string {
  return [product.sku, product.name].filter(Boolean).join(' · ')
}

function paymentDraftFromStoreMethod(method: ChannelStoreMethod): Partial<PaymentMappingDraft> {
  return {
    channelProvider: method.provider,
    channelConnectionId: String(method.channelConnectionId),
    selectedPaymentStoreMethodId: String(method.id),
    paymentMethodName: method.displayName,
    paymentMethodCode: method.code ?? '',
    paymentMethodType: method.subtype ?? '',
    paymentMethodExternalId: method.externalId,
  }
}

function shippingDraftFromStoreMethod(method: ChannelStoreMethod): Partial<ShippingMappingDraft> {
  return {
    channelProvider: method.provider,
    channelConnectionId: String(method.channelConnectionId),
    selectedShippingStoreMethodId: String(method.id),
    shippingMethodExternalId: method.externalId,
    shippingMethodCode: method.code ?? '',
    shippingMethod: method.displayName,
  }
}

function paymentShippingConditionFromStoreMethod(method: ChannelStoreMethod): Partial<PaymentMappingDraft> {
  return {
    channelProvider: method.provider,
    channelConnectionId: String(method.channelConnectionId),
    selectedShippingStoreMethodId: String(method.id),
    shippingMethodExternalId: method.externalId,
    shippingMethodCode: method.code ?? '',
    shippingMethod: method.displayName,
  }
}

function policyDefaultWarehouse(policy: ZohoOrderPolicy): string {
  return policy.defaultWarehouseExternalId ?? policy.warehouseExternalId ?? ''
}

function legacyActionMappings(policy: ZohoOrderPolicy): ActionMappingDraft[] {
  const mappings: ActionMappingDraft[] = [{ priority: '10', action: 'CREATE_SALES_ORDER', orderStatuses: ['NEW'], rawStatuses: '' }]
  if (policy.confirmOrder) mappings.push({ priority: '20', action: 'CONFIRM_SALES_ORDER', orderStatuses: ['IN_PROGRESS'], rawStatuses: '' })
  if (policy.createInvoice ?? true) mappings.push({ priority: '30', action: 'CREATE_INVOICE', orderStatuses: ['COMPLETED'], rawStatuses: '' })
  if (policy.voidOrder) mappings.push({ priority: '40', action: 'VOID_SALES_ORDER', orderStatuses: ['CANCELLED'], rawStatuses: '' })
  return mappings
}

function actionMappingsFromPolicy(policy: ZohoOrderPolicy): ActionMappingDraft[] {
  if (!policy.actionMappings?.length) return legacyActionMappings(policy)
  return policy.actionMappings.map(mapping => ({
    priority: String(mapping.priority ?? 100),
    action: mapping.action,
    orderStatuses: Array.from(new Set(mapping.statuses.map(status => status.orderStatus))),
    rawStatuses: Array.from(new Set(mapping.statuses.map(status => status.rawStatus?.trim()).filter(Boolean) as string[])).join(', '),
  }))
}

function warehouseOverridesFromPolicy(policy: ZohoOrderPolicy): WarehouseOverrideDraft[] {
  const source = policy.warehouseOverrides?.length
    ? policy.warehouseOverrides
    : (policy.exceptionRules ?? []).filter(rule => Boolean(rule.warehouseExternalId))
  return source.map(rule => ({
    priority: String(rule.priority ?? 100),
    channelProvider: rule.channelProvider ?? '',
    channelConnectionId: rule.channelConnectionId == null ? '' : String(rule.channelConnectionId),
    orderStatus: rule.orderStatus ?? '',
    rawStatus: rule.rawStatus ?? '',
    paymentStatus: rule.paymentStatus ?? '',
    paymentMethodCode: rule.paymentMethodCode ?? '',
    shippingCompany: rule.shippingCompany ?? '',
    shippingMethod: rule.shippingMethod ?? '',
    fulfillmentChannel: rule.fulfillmentChannel ?? '',
    fulfillmentStatus: rule.fulfillmentStatus ?? '',
    skuMatchScope: (rule.skuMatchScope ?? '') as ZohoOrderPolicySkuMatchScope | '',
    skuMatchOperator: (rule.skuMatchOperator ?? '') as ZohoOrderPolicySkuMatchOperator | '',
    skuMatchValue: rule.skuMatchValue ?? '',
    warehouseExternalId: rule.warehouseExternalId ?? '',
  }))
}

export function SyncRuleWizardPage({ accessToken, connectionId, policyId = null, onBack }: SyncRuleWizardPageProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [masterData, setMasterData] = useState<MasterDataState>(() => emptyMasterData())
  const [activePolicy, setActivePolicy] = useState<ZohoOrderPolicy | null>(null)
  const [policies, setPolicies] = useState<ZohoOrderPolicy[]>([])
  const [channelConnections, setChannelConnections] = useState<ChannelConnection[]>([])
  const [storeMethodsByConnectionId, setStoreMethodsByConnectionId] = useState<Record<number, ChannelStoreMethod[]>>({})
  const [loadedStoreMethodConnectionIds, setLoadedStoreMethodConnectionIds] = useState<number[]>([])
  const [refreshingStoreMethods, setRefreshingStoreMethods] = useState(false)
  const [storeMethodRefreshCategories, setStoreMethodRefreshCategories] = useState<Record<number, ChannelStoreMethodRefreshCategory[]>>({})
  const [serviceProductQuery, setServiceProductQuery] = useState('')
  const [serviceProductResults, setServiceProductResults] = useState<ProductListItem[]>([])
  const [serviceProductLoading, setServiceProductLoading] = useState(false)
  const [serviceProductSearched, setServiceProductSearched] = useState(false)
  const [serviceProductError, setServiceProductError] = useState<unknown>(null)
  const [loading, setLoading] = useState(connectionId != null)
  const [refreshing, setRefreshing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [togglingAutoSend, setTogglingAutoSend] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [noticeKey, setNoticeKey] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [selectedChannelConnectionIds, setSelectedChannelConnectionIds] = useState<number[]>([])
  const [invoiceSourceMode, setInvoiceSourceMode] = useState<ZohoInvoiceSourceMode>('LOCAL_ORDER')
  const [customerExternalId, setCustomerExternalId] = useState('')
  const [defaultWarehouseExternalId, setDefaultWarehouseExternalId] = useState('')
  const [defaultPaymentTermsExternalId, setDefaultPaymentTermsExternalId] = useState('')
  const [defaultDeliveryMethodExternalId, setDefaultDeliveryMethodExternalId] = useState('')
  const [salesPersonExternalId, setSalesPersonExternalId] = useState('')
  const [reportingTagExternalId, setReportingTagExternalId] = useState('')
  const [reportingTagOptionExternalId, setReportingTagOptionExternalId] = useState('')
  const [actionMappings, setActionMappings] = useState<ActionMappingDraft[]>(() => DEFAULT_ACTION_MAPPINGS)
  const [paymentMappings, setPaymentMappings] = useState<PaymentMappingDraft[]>([])
  const [shippingMappings, setShippingMappings] = useState<ShippingMappingDraft[]>([])
  const [warehouseOverrides, setWarehouseOverrides] = useState<WarehouseOverrideDraft[]>([])
  const [warehouseOverrideDraft, setWarehouseOverrideDraft] = useState<WarehouseOverrideDraft>(EMPTY_WAREHOUSE_OVERRIDE)
  const [paymentDraft, setPaymentDraft] = useState<PaymentMappingDraft>(EMPTY_PAYMENT_MAPPING)
  const [shippingDraft, setShippingDraft] = useState<ShippingMappingDraft>(EMPTY_SHIPPING_MAPPING)
  const [paymentDefinitionsOpen, setPaymentDefinitionsOpen] = useState(true)
  const [shippingDefinitionsOpen, setShippingDefinitionsOpen] = useState(true)
  const [loadedPolicyId, setLoadedPolicyId] = useState<number | null>(null)

  const activeItems = (category: ZohoMasterDataCategory) => masterData[category].filter(item => item.active)
  const paymentTerms = activeItems('PAYMENT_TERMS')
  const deliveryMethods = activeItems('DELIVERY_METHOD')
  const reportingTags = activeItems('REPORTING_TAG')
  const reportingTagOptions = activeItems('REPORTING_TAG_OPTION')
  const baseReportingTagOptions = reportingTagOptions.filter(item => !reportingTagExternalId || item.parentExternalId === reportingTagExternalId)
  const channelOptions = channelConnections.map(connection => ({ value: String(connection.id), label: `${connection.displayName} · ${connection.provider}` }))
  const selectedChannelConnections = channelConnections.filter(connection => selectedChannelConnectionIds.includes(connection.id))
  const selectedChannelOptions = (selectedChannelConnections.length > 0 ? selectedChannelConnections : channelConnections)
    .map(connection => ({ value: String(connection.id), label: `${connection.displayName} · ${connection.provider}` }))
  const selectedZidChannelConnections = selectedChannelConnections.filter(connection => connection.provider === 'ZID')
  const selectedZidChannelIds = selectedZidChannelConnections.map(connection => connection.id)
  const selectedStoreMethods = selectedZidChannelIds.flatMap(id => storeMethodsByConnectionId[id] ?? [])
  const paymentStoreMethods = selectedStoreMethods.filter(method => method.type === 'PAYMENT')
  const shippingStoreMethods = selectedStoreMethods.filter(method => method.type === 'SHIPPING')
  const hasSelectedZidChannels = selectedZidChannelIds.length > 0
  const hasMissingShippingScope = Object.values(storeMethodRefreshCategories).some(categories =>
    categories.some(category => category.type === 'SHIPPING' && category.status === 'MISSING_SCOPE')
  )
  const selectedPolicy = policyId == null ? null : policies.find(policy => policy.id === policyId) ?? null
  const isEditingDraft = selectedPolicy?.status === 'DRAFT'
  const isViewingLockedPolicy = selectedPolicy != null && (selectedPolicy.status !== 'DRAFT' || selectedPolicy.routingModelVersion === 2)
  const fieldsDisabled = isViewingLockedPolicy
  const selectedAutoSendEnabled = selectedPolicy?.autoSendEnabled === true
  const hasRequiredChannels = selectedChannelConnectionIds.length > 0
  const hasRequiredActionMappings = actionMappings.some(mapping => mapping.orderStatuses.length > 0)
  const paymentDraftNeedsCriteria = Boolean(paymentDraft.paymentTermsExternalId) && !hasPaymentMappingCriteria(paymentDraft)
  const shippingDraftNeedsCriteria = Boolean(shippingDraft.deliveryMethodExternalId) && !hasShippingMappingCriteria(shippingDraft)
  const hasInvalidPaymentMappings = paymentMappings.some(mapping => !mapping.paymentTermsExternalId || !hasPaymentMappingCriteria(mapping))
  const hasInvalidShippingMappings = shippingMappings.some(mapping => !mapping.deliveryMethodExternalId || !hasShippingMappingCriteria(mapping))
  const canSaveDraft = !fieldsDisabled && !saving && !loading && hasRequiredChannels && hasRequiredActionMappings && !hasInvalidPaymentMappings && !hasInvalidShippingMappings
  const singleColumnGrid = 'minmax(0, 1fr)'
  const mobileFullWidthButtonStyle = isMobile
    ? { width: '100%', justifyContent: 'center', whiteSpace: 'normal' as const, textAlign: 'center' as const, minWidth: 0 }
    : { minWidth: 0 }

  const activeSummary = useMemo(() => {
    if (!activePolicy) return null
    const warehouseName = masterData.WAREHOUSE.filter(item => item.active).find(item => item.externalId === policyDefaultWarehouse(activePolicy))?.displayName ?? t('pages.syncRuleWizard.emptyValue')
    return t('pages.syncRuleWizard.active.summary', {
      channels: activePolicy.channelConnectionIds?.length ?? 0,
      warehouse: warehouseName,
    })
  }, [activePolicy, masterData, t])

  useEffect(() => {
    if (connectionId == null) return
    let cancelled = false
    Promise.all([
      ...MASTER_DATA_CATEGORIES.map(category => listZohoMasterData(accessToken, connectionId, category)),
      getActiveZohoOrderPolicy(accessToken, connectionId).catch(caught => {
        if (caught instanceof ApiClientError && caught.code === 'ZOHO_ORDER_POLICY_NOT_FOUND') return null
        throw caught
      }),
      listZohoOrderPolicies(accessToken, connectionId),
      listChannelConnections(accessToken),
    ])
      .then(results => {
        if (cancelled) return
        const next = emptyMasterData()
        MASTER_DATA_CATEGORIES.forEach((category, index) => {
          next[category] = results[index] as ZohoMasterDataItem[]
        })
        setError(null)
        setMasterData(next)
        setActivePolicy(results[MASTER_DATA_CATEGORIES.length] as ZohoOrderPolicy | null)
        setPolicies(results[MASTER_DATA_CATEGORIES.length + 1] as ZohoOrderPolicy[])
        setChannelConnections(results[MASTER_DATA_CATEGORIES.length + 2] as ChannelConnection[])
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, connectionId])

  useEffect(() => {
    const missingIds = selectedZidChannelIds.filter(id => !loadedStoreMethodConnectionIds.includes(id))
    if (missingIds.length === 0) return
    let cancelled = false
    Promise.all(missingIds.map(async id => ({ id, methods: await listChannelStoreMethods(accessToken, id) })))
      .then(results => {
        if (cancelled) return
        setStoreMethodsByConnectionId(current => {
          const next = { ...current }
          results.forEach(result => {
            next[result.id] = result.methods
          })
          return next
        })
        setLoadedStoreMethodConnectionIds(current => Array.from(new Set([...current, ...results.map(result => result.id)])))
      })
      .catch(caught => {
        if (!cancelled) setError(caught)
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, loadedStoreMethodConnectionIds, selectedZidChannelIds])

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (policyId == null) {
      setLoadedPolicyId(null)
      return
    }
    const policy = policies.find(candidate => candidate.id === policyId)
    if (!policy || loadedPolicyId === policy.id) return

    setName(policy.name)
    setSelectedChannelConnectionIds(policy.channelConnectionIds ?? [])
    setInvoiceSourceMode(policy.invoiceSourceMode ?? 'LOCAL_ORDER')
    setCustomerExternalId(policy.customerExternalId ?? '')
    setDefaultWarehouseExternalId(policyDefaultWarehouse(policy))
    setDefaultPaymentTermsExternalId(policy.paymentMappings.find(isDefaultPaymentMapping)?.paymentTermsExternalId ?? policy.paymentTermsExternalId ?? '')
    setDefaultDeliveryMethodExternalId(policy.shippingMappings.find(isDefaultShippingMapping)?.deliveryMethodExternalId ?? policy.deliveryMethodExternalId ?? '')
    setSalesPersonExternalId(policy.salesPersonExternalId ?? '')
    setReportingTagExternalId(policy.reportingTagExternalId ?? '')
    setReportingTagOptionExternalId(policy.reportingTagOptionExternalId ?? '')
    setActionMappings(actionMappingsFromPolicy(policy))
    setPaymentMappings(policy.paymentMappings.filter(mapping => !isDefaultPaymentMapping(mapping)).map(mapping => ({
      criteriaFields: [],
      channelProvider: mapping.channelProvider ?? '',
      channelConnectionId: mapping.channelConnectionId == null ? '' : String(mapping.channelConnectionId),
      selectedPaymentStoreMethodId: '',
      selectedShippingStoreMethodId: '',
      paymentMethodName: mapping.paymentMethodName ?? '',
      paymentMethodCode: mapping.paymentMethodCode ?? '',
      paymentMethodType: mapping.paymentMethodType ?? '',
      paymentMethodExternalId: mapping.paymentMethodExternalId ?? '',
      paymentStatus: mapping.paymentStatus ?? '',
      shippingMethodExternalId: mapping.shippingMethodExternalId ?? '',
      shippingMethodCode: mapping.shippingMethodCode ?? '',
      shippingCompany: mapping.shippingCompany ?? '',
      shippingMethod: mapping.shippingMethod ?? '',
      paymentTermsExternalId: mapping.paymentTermsExternalId ?? '',
      paymentFeeServiceItemProductId: mapping.paymentFeeServiceItemProductId == null ? '' : String(mapping.paymentFeeServiceItemProductId),
      paymentFeeServiceItemLabel: mapping.paymentFeeServiceItemSku || mapping.paymentFeeServiceItemName
        ? [mapping.paymentFeeServiceItemSku, mapping.paymentFeeServiceItemName].filter(Boolean).join(' · ')
        : '',
    })).map(hydratePaymentCriteriaFields))
    setShippingMappings(policy.shippingMappings.filter(mapping => !isDefaultShippingMapping(mapping)).map(mapping => ({
      criteriaFields: [],
      channelProvider: mapping.channelProvider ?? '',
      channelConnectionId: mapping.channelConnectionId == null ? '' : String(mapping.channelConnectionId),
      selectedShippingStoreMethodId: '',
      shippingMethodExternalId: mapping.shippingMethodExternalId ?? '',
      shippingMethodCode: mapping.shippingMethodCode ?? '',
      shippingCompany: mapping.shippingCompany ?? '',
      shippingMethod: mapping.shippingMethod ?? '',
      deliveryMethodExternalId: mapping.deliveryMethodExternalId ?? '',
      shippingFeeServiceItemProductId: mapping.shippingFeeServiceItemProductId == null ? '' : String(mapping.shippingFeeServiceItemProductId),
      shippingFeeServiceItemLabel: mapping.shippingFeeServiceItemSku || mapping.shippingFeeServiceItemName
        ? [mapping.shippingFeeServiceItemSku, mapping.shippingFeeServiceItemName].filter(Boolean).join(' · ')
        : '',
    })).map(hydrateShippingCriteriaFields))
    setWarehouseOverrides(warehouseOverridesFromPolicy(policy))
    setWarehouseOverrideDraft(EMPTY_WAREHOUSE_OVERRIDE)
    setPaymentDraft(EMPTY_PAYMENT_MAPPING)
    setShippingDraft(EMPTY_SHIPPING_MAPPING)
    setLoadedPolicyId(policy.id)
  }, [loadedPolicyId, policies, policyId])
  /* eslint-enable react-hooks/set-state-in-effect */

  async function handleRefreshMasterData() {
    if (connectionId == null) return
    setRefreshing(true)
    setError(null)
    setNoticeKey(null)
    try {
      await refreshZohoMasterData(accessToken, connectionId, { categories: REFRESH_MASTER_DATA_CATEGORIES })
      const rows = await Promise.all(MASTER_DATA_CATEGORIES.map(category => listZohoMasterData(accessToken, connectionId, category)))
      const next = emptyMasterData()
      MASTER_DATA_CATEGORIES.forEach((category, index) => {
        next[category] = rows[index]
      })
      setMasterData(next)
      setNoticeKey('pages.syncRuleWizard.notices.masterDataRefreshed')
    } catch (caught) {
      setError(caught)
    } finally {
      setRefreshing(false)
    }
  }

  function addPaymentMapping() {
    if (!paymentDraft.paymentTermsExternalId || !hasPaymentMappingCriteria(paymentDraft)) return
    setPaymentMappings(current => [...current, { ...paymentDraft, criteriaFields: paymentCriteriaFieldsFor(paymentDraft) }])
    setPaymentDraft(EMPTY_PAYMENT_MAPPING)
  }

  function addShippingMapping() {
    if (!shippingDraft.deliveryMethodExternalId || !hasShippingMappingCriteria(shippingDraft)) return
    setShippingMappings(current => [...current, { ...shippingDraft, criteriaFields: shippingCriteriaFieldsFor(shippingDraft) }])
    setShippingDraft(EMPTY_SHIPPING_MAPPING)
  }

  function updatePaymentMapping(index: number, patch: Partial<PaymentMappingDraft>) {
    setPaymentMappings(current => current.map((mapping, currentIndex) => currentIndex === index ? { ...mapping, ...patch } : mapping))
  }

  function removePaymentMapping(index: number) {
    setPaymentMappings(current => current.filter((_, currentIndex) => currentIndex !== index))
  }

  function updateShippingMapping(index: number, patch: Partial<ShippingMappingDraft>) {
    setShippingMappings(current => current.map((mapping, currentIndex) => currentIndex === index ? { ...mapping, ...patch } : mapping))
  }

  function removeShippingMapping(index: number) {
    setShippingMappings(current => current.filter((_, currentIndex) => currentIndex !== index))
  }

  function addPaymentDraftCriteriaField(field: PaymentCriteriaField) {
    setPaymentDraft(current => ({ ...current, criteriaFields: uniqueCriteriaFields([...paymentCriteriaFieldsFor(current), field]) }))
  }

  function removePaymentDraftCriteriaField(field: PaymentCriteriaField) {
    setPaymentDraft(current => clearPaymentCriteriaField(current, field))
  }

  function addShippingDraftCriteriaField(field: ShippingCriteriaField) {
    setShippingDraft(current => ({ ...current, criteriaFields: uniqueCriteriaFields([...shippingCriteriaFieldsFor(current), field]) }))
  }

  function removeShippingDraftCriteriaField(field: ShippingCriteriaField) {
    setShippingDraft(current => clearShippingCriteriaField(current, field))
  }

  function addPaymentMappingCriteriaField(index: number, field: PaymentCriteriaField) {
    setPaymentMappings(current => current.map((mapping, currentIndex) => currentIndex === index
      ? { ...mapping, criteriaFields: uniqueCriteriaFields([...paymentCriteriaFieldsFor(mapping), field]) }
      : mapping))
  }

  function removePaymentMappingCriteriaField(index: number, field: PaymentCriteriaField) {
    setPaymentMappings(current => current.map((mapping, currentIndex) => currentIndex === index ? clearPaymentCriteriaField(mapping, field) : mapping))
  }

  function addShippingMappingCriteriaField(index: number, field: ShippingCriteriaField) {
    setShippingMappings(current => current.map((mapping, currentIndex) => currentIndex === index
      ? { ...mapping, criteriaFields: uniqueCriteriaFields([...shippingCriteriaFieldsFor(mapping), field]) }
      : mapping))
  }

  function removeShippingMappingCriteriaField(index: number, field: ShippingCriteriaField) {
    setShippingMappings(current => current.map((mapping, currentIndex) => currentIndex === index ? clearShippingCriteriaField(mapping, field) : mapping))
  }

  async function handleRefreshStoreMethods() {
    if (selectedZidChannelIds.length === 0) return
    setRefreshingStoreMethods(true)
    setError(null)
    setNoticeKey(null)
    try {
      const results = await Promise.all(selectedZidChannelIds.map(id => refreshChannelStoreMethods(accessToken, id)))
      setStoreMethodsByConnectionId(current => {
        const next = { ...current }
        results.forEach(result => {
          next[result.channelConnectionId] = result.methods
        })
        return next
      })
      setLoadedStoreMethodConnectionIds(current => Array.from(new Set([...current, ...results.map(result => result.channelConnectionId)])))
      setStoreMethodRefreshCategories(current => {
        const next = { ...current }
        results.forEach(result => {
          next[result.channelConnectionId] = result.categories
        })
        return next
      })
      setNoticeKey('pages.syncRuleWizard.notices.channelMethodsRefreshed')
    } catch (caught) {
      setError(caught)
    } finally {
      setRefreshingStoreMethods(false)
    }
  }

  async function handleSearchServiceProducts() {
    setServiceProductLoading(true)
    setServiceProductError(null)
    setServiceProductSearched(true)
    try {
      const page = await listProducts(accessToken, { page: 0, size: 8, query: serviceProductQuery })
      setServiceProductResults(page.items)
    } catch (caught) {
      setServiceProductError(caught)
      setServiceProductResults([])
    } finally {
      setServiceProductLoading(false)
    }
  }

  function updateActionMapping(index: number, patch: Partial<ActionMappingDraft>) {
    setActionMappings(current => current.map((mapping, currentIndex) => currentIndex === index ? { ...mapping, ...patch } : mapping))
  }

  function removeActionMapping(index: number) {
    setActionMappings(current => current.filter((_, currentIndex) => currentIndex !== index))
  }

  function addActionMapping() {
    setActionMappings(current => [...current, { priority: String((current.length + 1) * 10), action: 'CREATE_SALES_ORDER', orderStatuses: ['NEW'], rawStatuses: '' }])
  }

  function updateWarehouseOverrideDraft(patch: Partial<WarehouseOverrideDraft>) {
    setWarehouseOverrideDraft(current => ({ ...current, ...patch }))
  }

  function applyFlexPreset() {
    updateWarehouseOverrideDraft({ skuMatchScope: 'ANY_LINE', skuMatchOperator: 'PREFIX', skuMatchValue: 'F-' })
  }

  function applyFbnPreset() {
    updateWarehouseOverrideDraft({ fulfillmentChannel: 'FBN' })
  }

  function addWarehouseOverride() {
    if (!hasWarehouseOverrideCondition(warehouseOverrideDraft) || !warehouseOverrideDraft.warehouseExternalId) return
    setWarehouseOverrides(current => [...current, warehouseOverrideDraft])
    setWarehouseOverrideDraft({ ...EMPTY_WAREHOUSE_OVERRIDE, priority: String((warehouseOverrides.length + 2) * 10) })
  }

  function removeWarehouseOverride(index: number) {
    setWarehouseOverrides(current => current.filter((_, currentIndex) => currentIndex !== index))
  }

  function warehouseOverridePayload(rule: WarehouseOverrideDraft, index: number): ZohoOrderPolicyWarehouseOverrideRuleRequest {
    const channelConnectionId = optional(rule.channelConnectionId)
    return {
      priority: Number(rule.priority) || index + 1,
      channelProvider: optional(rule.channelProvider),
      channelConnectionId: channelConnectionId == null ? null : Number(channelConnectionId),
      orderStatus: optional(rule.orderStatus),
      rawStatus: optional(rule.rawStatus),
      paymentStatus: optional(rule.paymentStatus),
      paymentMethodCode: optional(rule.paymentMethodCode),
      shippingCompany: optional(rule.shippingCompany),
      shippingMethod: optional(rule.shippingMethod),
      fulfillmentChannel: optional(rule.fulfillmentChannel),
      fulfillmentStatus: optional(rule.fulfillmentStatus),
      skuMatchScope: rule.skuMatchValue.trim() ? rule.skuMatchScope || null : null,
      skuMatchOperator: rule.skuMatchValue.trim() ? rule.skuMatchOperator || null : null,
      skuMatchValue: optional(rule.skuMatchValue),
      warehouseExternalId: rule.warehouseExternalId,
    }
  }

  async function handleToggleAutoSend(enabled: boolean) {
    if (connectionId == null || !selectedPolicy) return
    setTogglingAutoSend(true)
    setError(null)
    setNoticeKey(null)
    try {
      const updated = await setZohoOrderPolicyAutoSend(accessToken, connectionId, selectedPolicy.id, { enabled })
      setPolicies(current => current.map(existing => existing.id === updated.id ? updated : existing))
      setActivePolicy(current => current?.id === updated.id ? updated : current)
    } catch (caught) {
      setError(caught)
    } finally {
      setTogglingAutoSend(false)
    }
  }

  async function handleSaveDraft() {
    if (connectionId == null) return
    setSaving(true)
    setError(null)
    setNoticeKey(null)
    const paymentMappingsPayload = paymentMappings.map((mapping, index) => ({
      priority: index + 1,
      ...paymentCriteriaPayload(mapping),
      paymentTermsExternalId: mapping.paymentTermsExternalId || null,
      paymentFeeServiceItemProductId: optional(mapping.paymentFeeServiceItemProductId) == null ? null : Number(mapping.paymentFeeServiceItemProductId),
    }))
    if (defaultPaymentTermsExternalId) {
      paymentMappingsPayload.push({
        priority: 10000,
        channelProvider: null,
        channelConnectionId: null,
        paymentMethodName: null,
        paymentStatus: null,
        paymentMethodCode: null,
        paymentMethodType: null,
        paymentMethodExternalId: null,
        shippingMethodExternalId: null,
        shippingMethodCode: null,
        shippingCompany: null,
        shippingMethod: null,
        paymentTermsExternalId: defaultPaymentTermsExternalId,
        paymentFeeServiceItemProductId: null,
      })
    }
    const shippingMappingsPayload = shippingMappings.map((mapping, index) => ({
      priority: index + 1,
      ...shippingCriteriaPayload(mapping),
      deliveryMethodExternalId: mapping.deliveryMethodExternalId || null,
      shippingFeeServiceItemProductId: optional(mapping.shippingFeeServiceItemProductId) == null ? null : Number(mapping.shippingFeeServiceItemProductId),
    }))
    if (defaultDeliveryMethodExternalId) {
      shippingMappingsPayload.push({
        priority: 10000,
        channelProvider: null,
        channelConnectionId: null,
        shippingMethodExternalId: null,
        shippingMethodCode: null,
        shippingCompany: null,
        shippingMethod: null,
        deliveryMethodExternalId: defaultDeliveryMethodExternalId,
        shippingFeeServiceItemProductId: null,
      })
    }
    const payload: ZohoOrderPolicySaveRequest = {
      name: name.trim() || String(t('pages.syncRuleWizard.defaults.policyName')),
      channelConnectionIds: selectedChannelConnectionIds,
      invoiceSourceMode,
      customerExternalId: customerExternalId || null,
      defaultWarehouseExternalId: defaultWarehouseExternalId || null,
      salesPersonExternalId: salesPersonExternalId || null,
      reportingTagExternalId: reportingTagExternalId || null,
      reportingTagOptionExternalId: reportingTagOptionExternalId || null,
      actionMappings: actionMappings
        .filter(mapping => mapping.orderStatuses.length > 0)
        .map((mapping, index) => ({
          priority: Number(mapping.priority) || index + 1,
          action: mapping.action,
          orderStatuses: mapping.orderStatuses,
          rawStatuses: parseDelimitedValues(mapping.rawStatuses),
        })),
      paymentMappings: paymentMappingsPayload,
      shippingMappings: shippingMappingsPayload,
      warehouseOverrides: warehouseOverrides.map(warehouseOverridePayload),
    }
    try {
      const policy = isEditingDraft && selectedPolicy
        ? await updateZohoOrderPolicy(accessToken, connectionId, selectedPolicy.id, payload)
        : await createZohoOrderPolicy(accessToken, connectionId, payload)
      setPolicies(current => [policy, ...current.filter(existing => existing.id !== policy.id)])
      setLoadedPolicyId(policy.id)
      setNoticeKey(isEditingDraft ? 'pages.syncRuleWizard.notices.draftUpdated' : 'pages.syncRuleWizard.notices.draftCreated')
    } catch (caught) {
      setError(caught)
    } finally {
      setSaving(false)
    }
  }

  if (connectionId == null) {
    return (
      <div style={{ paddingBlock: isMobile ? 0 : 24, paddingInline: isMobile ? 0 : 28, maxWidth: 980, margin: '0 auto', boxSizing: 'border-box', minWidth: 0, width: '100%' }}>
        <Card padding={24}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.noConnection.title')}</h2>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.noConnection.body')}</p>
          <Button kind="secondary" size="sm" onClick={onBack}>{t('pages.syncRuleWizard.actions.backToZoho')}</Button>
        </Card>
      </div>
    )
  }

  const skuScopeOptions = [
    { value: 'ANY_LINE', label: t('pages.syncRuleWizard.skuScopes.ANY_LINE') },
    { value: 'ALL_LINES', label: t('pages.syncRuleWizard.skuScopes.ALL_LINES') },
    { value: 'NO_LINE', label: t('pages.syncRuleWizard.skuScopes.NO_LINE') },
  ]
  const skuOperatorOptions = [
    { value: 'EXACT', label: t('pages.syncRuleWizard.skuOperators.EXACT') },
    { value: 'PREFIX', label: t('pages.syncRuleWizard.skuOperators.PREFIX') },
    { value: 'SUFFIX', label: t('pages.syncRuleWizard.skuOperators.SUFFIX') },
    { value: 'CONTAINS', label: t('pages.syncRuleWizard.skuOperators.CONTAINS') },
  ]
  const actionOptions = LIFECYCLE_ACTIONS.map(value => ({ value, label: t(`pages.syncRuleWizard.lifecycleActions.${value}`) }))
  const orderStatusOptions = ORDER_STATUSES.map(value => ({ value, label: t(`pages.syncRuleWizard.orderStatuses.${value}`) }))
  const invoiceSourceOptions = INVOICE_SOURCE_MODES.map(value => ({ value, label: t(`pages.syncRuleWizard.invoiceSource.modes.${value}`) }))
  const paymentMethodOptionsFor = (channelConnectionId: string) => paymentStoreMethods
    .filter(method => !channelConnectionId || String(method.channelConnectionId) === channelConnectionId)
    .map(method => ({ value: String(method.id), label: storeMethodLabel(method, channelConnections.find(connection => connection.id === method.channelConnectionId)) }))
  const shippingMethodOptionsFor = (channelConnectionId: string) => shippingStoreMethods
    .filter(method => !channelConnectionId || String(method.channelConnectionId) === channelConnectionId)
    .map(method => ({ value: String(method.id), label: storeMethodLabel(method, channelConnections.find(connection => connection.id === method.channelConnectionId)) }))

  const paymentCriteriaLabels: Record<PaymentCriteriaField, string> = {
    channelScope: t('pages.syncRuleWizard.fields.mappingChannelScope'),
    channelPaymentMethod: t('pages.syncRuleWizard.fields.channelPaymentMethod'),
    shippingCondition: t('pages.syncRuleWizard.fields.shippingCondition'),
    paymentMethodName: t('pages.syncRuleWizard.fields.paymentMethod'),
    paymentMethodCode: t('pages.syncRuleWizard.fields.paymentMethodCode'),
    paymentMethodType: t('pages.syncRuleWizard.fields.paymentMethodType'),
    paymentMethodExternalId: t('pages.syncRuleWizard.fields.paymentMethodExternalId'),
    paymentStatus: t('pages.syncRuleWizard.fields.paymentStatus'),
    shippingMethodExternalId: t('pages.syncRuleWizard.fields.shippingMethodExternalId'),
    shippingMethodCode: t('pages.syncRuleWizard.fields.shippingMethodCode'),
    shippingCompany: t('pages.syncRuleWizard.fields.shippingCompany'),
    shippingMethod: t('pages.syncRuleWizard.fields.shippingMethod'),
  }
  const shippingCriteriaLabels: Record<ShippingCriteriaField, string> = {
    channelScope: t('pages.syncRuleWizard.fields.mappingChannelScope'),
    channelDeliveryOption: t('pages.syncRuleWizard.fields.channelDeliveryOption'),
    shippingMethodExternalId: t('pages.syncRuleWizard.fields.shippingMethodExternalId'),
    shippingMethodCode: t('pages.syncRuleWizard.fields.shippingMethodCode'),
    shippingCompany: t('pages.syncRuleWizard.fields.shippingCompany'),
    shippingMethod: t('pages.syncRuleWizard.fields.shippingMethod'),
  }
  const criterionGridColumns = isMobile ? singleColumnGrid : 'repeat(2, minmax(0, 1fr))'
  const conditionRemoveButtonStyle = {
    alignSelf: 'end',
    justifySelf: isMobile ? 'start' : 'end',
    background: 'transparent',
    border: 'none',
    color: 'var(--danger)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: 800,
    padding: '9px 0',
  }

  function paymentCriteriaOptions(activeFields: PaymentCriteriaField[]) {
    return PAYMENT_CRITERIA_FIELD_ORDER
      .filter(field => !activeFields.includes(field))
      .map(field => ({ value: field, label: paymentCriteriaLabels[field] }))
  }

  function shippingCriteriaOptions(activeFields: ShippingCriteriaField[]) {
    return SHIPPING_CRITERIA_FIELD_ORDER
      .filter(field => !activeFields.includes(field))
      .map(field => ({ value: field, label: shippingCriteriaLabels[field] }))
  }

  function paymentCriterionControl(mapping: PaymentMappingDraft, field: PaymentCriteriaField, patch: (patch: Partial<PaymentMappingDraft>) => void) {
    if (field === 'channelScope') {
      return <OptionSelect label={paymentCriteriaLabels[field]} value={mapping.channelConnectionId} onChange={value => patch({ channelConnectionId: value, channelProvider: channelProviderForId(channelConnections, value), ...clearPaymentStoreMethodValues(mapping), ...clearPaymentShippingConditionValues(mapping) })} options={selectedChannelOptions} placeholder={t('pages.syncRuleWizard.placeholders.allPolicyChannels')} disabled={fieldsDisabled} />
    }
    if (field === 'channelPaymentMethod') {
      const options = paymentMethodOptionsFor(mapping.channelConnectionId)
      return <OptionSelect label={paymentCriteriaLabels[field]} value={mapping.selectedPaymentStoreMethodId} onChange={value => {
        const method = paymentStoreMethods.find(candidate => String(candidate.id) === value)
        patch(method ? paymentDraftFromStoreMethod(method) : clearPaymentStoreMethodValues(mapping))
      }} options={options} placeholder={t('pages.syncRuleWizard.placeholders.manualOrObserved')} disabled={fieldsDisabled || options.length === 0} />
    }
    if (field === 'shippingCondition') {
      const options = shippingMethodOptionsFor(mapping.channelConnectionId)
      return <OptionSelect label={paymentCriteriaLabels[field]} value={mapping.selectedShippingStoreMethodId} onChange={value => {
        const method = shippingStoreMethods.find(candidate => String(candidate.id) === value)
        patch(method ? paymentShippingConditionFromStoreMethod(method) : clearPaymentShippingConditionValues(mapping))
      }} options={options} placeholder={t('pages.syncRuleWizard.placeholders.noShippingCondition')} disabled={fieldsDisabled || options.length === 0} />
    }
    if (field === 'paymentMethodName') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.paymentMethodName} onChange={value => patch({ paymentMethodName: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentMethod')} disabled={fieldsDisabled} />
    if (field === 'paymentMethodCode') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.paymentMethodCode} onChange={value => patch({ paymentMethodCode: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentMethodCode')} disabled={fieldsDisabled} />
    if (field === 'paymentMethodType') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.paymentMethodType} onChange={value => patch({ paymentMethodType: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentMethodType')} disabled={fieldsDisabled} />
    if (field === 'paymentMethodExternalId') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.paymentMethodExternalId} onChange={value => patch({ paymentMethodExternalId: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentMethodExternalId')} disabled={fieldsDisabled} />
    if (field === 'paymentStatus') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.paymentStatus} onChange={value => patch({ paymentStatus: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentStatus')} disabled={fieldsDisabled} />
    if (field === 'shippingMethodExternalId') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.shippingMethodExternalId} onChange={value => patch({ shippingMethodExternalId: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethodExternalId')} disabled={fieldsDisabled} />
    if (field === 'shippingMethodCode') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.shippingMethodCode} onChange={value => patch({ shippingMethodCode: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethodCode')} disabled={fieldsDisabled} />
    if (field === 'shippingCompany') return <TextInput label={paymentCriteriaLabels[field]} value={mapping.shippingCompany} onChange={value => patch({ shippingCompany: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingCompany')} disabled={fieldsDisabled} />
    return <TextInput label={paymentCriteriaLabels[field]} value={mapping.shippingMethod} onChange={value => patch({ shippingMethod: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethod')} disabled={fieldsDisabled} />
  }

  function shippingCriterionControl(mapping: ShippingMappingDraft, field: ShippingCriteriaField, patch: (patch: Partial<ShippingMappingDraft>) => void) {
    if (field === 'channelScope') {
      return <OptionSelect label={shippingCriteriaLabels[field]} value={mapping.channelConnectionId} onChange={value => patch({ channelConnectionId: value, channelProvider: channelProviderForId(channelConnections, value), ...clearShippingStoreMethodValues(mapping) })} options={selectedChannelOptions} placeholder={t('pages.syncRuleWizard.placeholders.allPolicyChannels')} disabled={fieldsDisabled} />
    }
    if (field === 'channelDeliveryOption') {
      const options = shippingMethodOptionsFor(mapping.channelConnectionId)
      return <OptionSelect label={shippingCriteriaLabels[field]} value={mapping.selectedShippingStoreMethodId} onChange={value => {
        const method = shippingStoreMethods.find(candidate => String(candidate.id) === value)
        patch(method ? shippingDraftFromStoreMethod(method) : clearShippingStoreMethodValues(mapping))
      }} options={options} placeholder={t('pages.syncRuleWizard.placeholders.manualOrObserved')} disabled={fieldsDisabled || options.length === 0} />
    }
    if (field === 'shippingMethodExternalId') return <TextInput label={shippingCriteriaLabels[field]} value={mapping.shippingMethodExternalId} onChange={value => patch({ shippingMethodExternalId: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethodExternalId')} disabled={fieldsDisabled} />
    if (field === 'shippingMethodCode') return <TextInput label={shippingCriteriaLabels[field]} value={mapping.shippingMethodCode} onChange={value => patch({ shippingMethodCode: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethodCode')} disabled={fieldsDisabled} />
    if (field === 'shippingCompany') return <TextInput label={shippingCriteriaLabels[field]} value={mapping.shippingCompany} onChange={value => patch({ shippingCompany: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingCompany')} disabled={fieldsDisabled} />
    return <TextInput label={shippingCriteriaLabels[field]} value={mapping.shippingMethod} onChange={value => patch({ shippingMethod: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethod')} disabled={fieldsDisabled} />
  }

  function renderServiceProductField({
    label,
    helper,
    selectedId,
    selectedLabel,
    onSelect,
  }: {
    label: string
    helper: string
    selectedId: string
    selectedLabel: string
    onSelect: (productId: string, label: string) => void
  }) {
    return (
      <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
        <div style={{ display: 'grid', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>{label}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{helper}</span>
        </div>
        {selectedId ? (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center', padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'white', minWidth: 0 }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)', minWidth: 0, overflowWrap: 'break-word' }}>
              {selectedLabel || t('pages.syncRuleWizard.serviceItems.selectedProduct', { id: selectedId })}
            </span>
            {!fieldsDisabled && (
              <button type="button" onClick={() => onSelect('', '')} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800, padding: 0 }}>
                {t('pages.syncRuleWizard.actions.clear')}
              </button>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.serviceItems.empty')}</div>
        )}
        {!fieldsDisabled && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end', minWidth: 0 }}>
              <TextInput label={t('pages.syncRuleWizard.fields.serviceItemSearch')} value={serviceProductQuery} onChange={setServiceProductQuery} placeholder={t('pages.syncRuleWizard.placeholders.searchServiceItems')} />
              <Button kind="secondary" size="sm" icon={I.search} onClick={handleSearchServiceProducts} disabled={serviceProductLoading} style={mobileFullWidthButtonStyle}>
                {serviceProductLoading ? t('pages.syncRuleWizard.serviceItems.searching') : t('pages.syncRuleWizard.actions.searchProducts')}
              </Button>
            </div>
            {serviceProductError !== null && <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>{t('pages.syncRuleWizard.serviceItems.error')}</div>}
            {serviceProductSearched && serviceProductResults.length === 0 && serviceProductError === null && !serviceProductLoading && (
              <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.serviceItems.noResults')}</div>
            )}
            {serviceProductResults.length > 0 && (
              <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                {serviceProductResults.map(product => {
                  const labelText = productServiceItemLabel(product)
                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => onSelect(String(product.id), labelText)}
                      style={{ display: 'grid', gap: 2, textAlign: 'start', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'white', padding: '8px 10px', cursor: 'pointer', fontFamily: 'inherit', minWidth: 0 }}
                    >
                      <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)', minWidth: 0, overflowWrap: 'break-word' }}>{product.name}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)', minWidth: 0, overflowWrap: 'break-word' }}>{product.sku}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  function renderPaymentCriteriaEditor(mapping: PaymentMappingDraft, patch: (patch: Partial<PaymentMappingDraft>) => void, addField: (field: PaymentCriteriaField) => void, removeField: (field: PaymentCriteriaField) => void) {
    const activeFields = paymentCriteriaFieldsFor(mapping)
    const addOptions = paymentCriteriaOptions(activeFields)
    return (
      <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
        <div style={{ display: 'grid', gap: 3, padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)' }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.mappingSections.orderConditionsTitle')}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.mappingSections.paymentOrderConditionsBody')}</span>
        </div>
        {activeFields.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', padding: '10px 12px', border: '1px dashed var(--border-default)', borderRadius: 10 }}>{t('pages.syncRuleWizard.mappingSections.noConditions')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: criterionGridColumns, gap: 10, minWidth: 0 }}>
            {activeFields.map(field => (
              <div key={field} style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end', minWidth: 0 }}>
                {paymentCriterionControl(mapping, field, patch)}
                {!fieldsDisabled && <button aria-label={t('pages.syncRuleWizard.actions.removeCondition', { condition: paymentCriteriaLabels[field] })} onClick={() => removeField(field)} style={conditionRemoveButtonStyle}>{t('pages.syncRuleWizard.actions.remove')}</button>}
              </div>
            ))}
          </div>
        )}
        {!fieldsDisabled && (
          <div style={{ maxWidth: isMobile ? '100%' : 340 }}>
            <OptionSelect label={t('pages.syncRuleWizard.fields.addPaymentCondition')} value="" onChange={value => { if (value) addField(value as PaymentCriteriaField) }} options={addOptions} placeholder={addOptions.length === 0 ? t('pages.syncRuleWizard.placeholders.allConditionsAdded') : t('pages.syncRuleWizard.placeholders.addCondition')} disabled={addOptions.length === 0} />
          </div>
        )}
      </div>
    )
  }

  function renderShippingCriteriaEditor(mapping: ShippingMappingDraft, patch: (patch: Partial<ShippingMappingDraft>) => void, addField: (field: ShippingCriteriaField) => void, removeField: (field: ShippingCriteriaField) => void) {
    const activeFields = shippingCriteriaFieldsFor(mapping)
    const addOptions = shippingCriteriaOptions(activeFields)
    return (
      <div style={{ display: 'grid', gap: 10, minWidth: 0 }}>
        <div style={{ display: 'grid', gap: 3, padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)' }}>
          <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.mappingSections.orderConditionsTitle')}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.mappingSections.shippingOrderConditionsBody')}</span>
        </div>
        {activeFields.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)', padding: '10px 12px', border: '1px dashed var(--border-default)', borderRadius: 10 }}>{t('pages.syncRuleWizard.mappingSections.noConditions')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: criterionGridColumns, gap: 10, minWidth: 0 }}>
            {activeFields.map(field => (
              <div key={field} style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end', minWidth: 0 }}>
                {shippingCriterionControl(mapping, field, patch)}
                {!fieldsDisabled && <button aria-label={t('pages.syncRuleWizard.actions.removeCondition', { condition: shippingCriteriaLabels[field] })} onClick={() => removeField(field)} style={conditionRemoveButtonStyle}>{t('pages.syncRuleWizard.actions.remove')}</button>}
              </div>
            ))}
          </div>
        )}
        {!fieldsDisabled && (
          <div style={{ maxWidth: isMobile ? '100%' : 340 }}>
            <OptionSelect label={t('pages.syncRuleWizard.fields.addShippingCondition')} value="" onChange={value => { if (value) addField(value as ShippingCriteriaField) }} options={addOptions} placeholder={addOptions.length === 0 ? t('pages.syncRuleWizard.placeholders.allConditionsAdded') : t('pages.syncRuleWizard.placeholders.addCondition')} disabled={addOptions.length === 0} />
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={{ paddingBlock: isMobile ? 0 : 24, paddingInline: isMobile ? 0 : 28, maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box', minWidth: 0, width: '100%' }}> 
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', rowGap: 6, minWidth: 0, marginBottom: 18, fontSize: 12.5, color: 'var(--fg-subtle)' }}> 
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'inherit', fontSize: 12.5 }}>{t('pages.syncRuleWizard.breadcrumb.inventory')}</button>
        <span>/</span>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--fg-muted)', fontFamily: 'inherit', fontSize: 12.5 }}>{t('pages.syncRuleWizard.breadcrumb.zoho')}</button>
        <span>/</span>
        <span style={{ color: 'var(--fg)', fontWeight: 600 }}>{t('pages.syncRuleWizard.breadcrumb.orderPolicy')}</span>
      </div>

      <div data-testid="sync-rule-main-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) 340px', gap: 22, alignItems: 'start', minWidth: 0 }}>
        <div style={{ display: 'grid', gap: 18, minWidth: 0 }}>
          <Card padding={24}>
            <div data-testid="sync-rule-header-row" style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? singleColumnGrid : undefined, justifyContent: 'space-between', gap: 14, alignItems: isMobile ? 'stretch' : 'flex-start', minWidth: 0 }}> 
              <div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--brand-900)', margin: 0, minWidth: 0, overflowWrap: 'break-word' }}>{t('pages.syncRuleWizard.title')}</h2>
                  {loading && <Pill tone="neutral">{t('pages.syncRuleWizard.loading')}</Pill>}
                </div>
                <p style={{ fontSize: 13.5, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.subtitle')}</p>
              </div>
              <Button kind="secondary" size="sm" icon={I.refresh} onClick={handleRefreshMasterData} disabled={refreshing || loading} style={mobileFullWidthButtonStyle}>
                {refreshing ? t('pages.syncRuleWizard.actions.refreshingMasterData') : t('pages.syncRuleWizard.actions.refreshMasterData')}
              </Button>
            </div>
            {noticeKey && (
              <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--success-100)', color: 'var(--success)', fontSize: 13, fontWeight: 700 }}>
                {t(noticeKey)}
              </div>
            )}
            {error !== null && (
              <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)', fontSize: 13, lineHeight: 1.45 }}>
                {String(t(errorMessageKey(error), { defaultValue: String(t('pages.syncRuleWizard.errors.codes.REQUEST_FAILED')) }))}
              </div>
            )}
          </Card>

          <Card padding={24}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.channels.title')}</h3>
            <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: '6px 0 18px', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.channels.body')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'repeat(2, minmax(0, 1fr))', gap: 10, minWidth: 0 }}>
              {channelConnections.length === 0 ? (
                <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.channels.empty')}</div>
              ) : channelConnections.map(connection => (
                <CheckboxRow
                  key={connection.id}
                  label={`${connection.displayName} · ${connection.provider}`}
                  checked={selectedChannelConnectionIds.includes(connection.id)}
                  disabled={fieldsDisabled}
                  onChange={checked => setSelectedChannelConnectionIds(current => checked ? [...current, connection.id] : current.filter(id => id !== connection.id))}
                />
              ))}
            </div>
            <div style={{ marginTop: 18, padding: 14, border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'var(--ink-50)' }}>
              <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.fields.invoiceSourceMode')}</span>
                <select
                  value={invoiceSourceMode}
                  onChange={event => setInvoiceSourceMode(event.target.value as ZohoInvoiceSourceMode)}
                  disabled={fieldsDisabled}
                  style={fieldStyle()}
                >
                  {invoiceSourceOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
              <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: '8px 0 0', lineHeight: 1.5 }}>
                {t(`pages.syncRuleWizard.invoiceSource.help.${invoiceSourceMode}`)}
              </p>
              <p style={{ fontSize: 12, color: 'var(--fg-subtle)', margin: '6px 0 0', lineHeight: 1.45 }}>
                {t('pages.syncRuleWizard.invoiceSource.scopeHelp')}
              </p>
            </div>
          </Card>

          <Card padding={24}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.commonDefaults.title')}</h3>
            <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: '6px 0 18px', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.commonDefaults.body')}</p>
            <div data-testid="sync-rule-base-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'repeat(2, minmax(0, 1fr))', gap: 14, minWidth: 0 }}>
              <TextInput label={t('pages.syncRuleWizard.fields.name')} value={name} onChange={setName} placeholder={t('pages.syncRuleWizard.defaults.policyName')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.customer')} value={customerExternalId} onChange={setCustomerExternalId} items={activeItems('CUSTOMER')} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.defaultWarehouse')} value={defaultWarehouseExternalId} onChange={setDefaultWarehouseExternalId} items={activeItems('WAREHOUSE')} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.defaultPaymentTerms')} value={defaultPaymentTermsExternalId} onChange={setDefaultPaymentTermsExternalId} items={paymentTerms} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.defaultDeliveryMethod')} value={defaultDeliveryMethodExternalId} onChange={setDefaultDeliveryMethodExternalId} items={deliveryMethods} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.salesPerson')} value={salesPersonExternalId} onChange={setSalesPersonExternalId} items={activeItems('SALES_PERSON')} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.reportingTag')} value={reportingTagExternalId} onChange={value => { setReportingTagExternalId(value); setReportingTagOptionExternalId('') }} items={reportingTags} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
              <SelectField label={t('pages.syncRuleWizard.fields.reportingTagOption')} value={reportingTagOptionExternalId} onChange={setReportingTagOptionExternalId} items={baseReportingTagOptions} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
            </div>
          </Card>

          <Card padding={24}>
            <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? singleColumnGrid : undefined, justifyContent: 'space-between', gap: 12, alignItems: isMobile ? 'stretch' : 'flex-start', minWidth: 0 }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.actionMappings.title')}</h3>
                <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: '6px 0 18px', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.actionMappings.body')}</p>
              </div>
              {!fieldsDisabled && <Button kind="secondary" size="sm" onClick={() => setActionMappings(DEFAULT_ACTION_MAPPINGS)} style={mobileFullWidthButtonStyle}>{t('pages.syncRuleWizard.actions.resetDefaultActions')}</Button>}
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {actionMappings.map((mapping, index) => (
                <div key={`${mapping.action}-${index}`} style={{ display: 'grid', gap: 10, padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'var(--ink-50)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : '90px minmax(0, 1fr) minmax(0, 1fr)', gap: 10 }}>
                    <TextInput label={t('pages.syncRuleWizard.fields.mappingPriority')} value={mapping.priority} onChange={value => updateActionMapping(index, { priority: value })} placeholder={t('pages.syncRuleWizard.placeholders.priority')} disabled={fieldsDisabled} />
                    <OptionSelect label={t('pages.syncRuleWizard.fields.lifecycleAction')} value={mapping.action} onChange={value => updateActionMapping(index, { action: value as ZohoOrderLifecycleAction })} options={actionOptions} placeholder={t('pages.syncRuleWizard.placeholders.selectRequired')} disabled={fieldsDisabled} />
                    <TextInput label={t('pages.syncRuleWizard.fields.rawStatuses')} value={mapping.rawStatuses} onChange={value => updateActionMapping(index, { rawStatuses: value })} placeholder={t('pages.syncRuleWizard.placeholders.rawStatuses')} disabled={fieldsDisabled} />
                  </div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.fields.orderStatuses')}</span>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'repeat(5, minmax(0, 1fr))', gap: 8 }}>
                      {ORDER_STATUSES.map(status => (
                        <CheckboxRow
                          key={status}
                          label={t(`pages.syncRuleWizard.orderStatuses.${status}`)}
                          checked={mapping.orderStatuses.includes(status)}
                          disabled={fieldsDisabled}
                          onChange={checked => updateActionMapping(index, { orderStatuses: toggleValue(mapping.orderStatuses, status, checked) })}
                        />
                      ))}
                    </div>
                  </div>
                  {!fieldsDisabled && actionMappings.length > 1 && (
                    <button onClick={() => removeActionMapping(index)} style={{ justifySelf: 'start', background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800 }}>{t('pages.syncRuleWizard.actions.remove')}</button>
                  )}
                </div>
              ))}
            </div>
            {!fieldsDisabled && <div style={{ marginTop: 12 }}><Button kind="secondary" size="sm" onClick={addActionMapping}>{t('pages.syncRuleWizard.actions.addActionMapping')}</Button></div>}
          </Card>

          <Card padding={24}>
            <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? singleColumnGrid : undefined, justifyContent: 'space-between', gap: 12, alignItems: isMobile ? 'stretch' : 'flex-start', minWidth: 0 }}>
              <button type="button" aria-expanded={paymentDefinitionsOpen} onClick={() => setPaymentDefinitionsOpen(open => !open)} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 10, alignItems: 'start', background: 'transparent', border: 'none', padding: 0, textAlign: 'start', cursor: 'pointer', fontFamily: 'inherit', minWidth: 0 }}>
                <Icon size={16} color="var(--fg-muted)" style={{ transform: paymentDefinitionsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform var(--dur-fast) var(--ease-out)', marginTop: 2 }}>{I.chevronD}</Icon>
                <span style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.paymentMappings.title')}</span>
                    <Pill tone="neutral" size="sm">{t('pages.syncRuleWizard.mappingSections.definitionsCount', { count: paymentMappings.length })}</Pill>
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.paymentMappings.body')}</span>
                </span>
              </button>
              {hasSelectedZidChannels && !fieldsDisabled && paymentDefinitionsOpen && (
                <Button kind="secondary" size="sm" icon={I.refresh} onClick={handleRefreshStoreMethods} disabled={refreshingStoreMethods} style={mobileFullWidthButtonStyle}>
                  {refreshingStoreMethods ? t('pages.syncRuleWizard.actions.refreshingChannelMethods') : t('pages.syncRuleWizard.actions.refreshChannelMethods')}
                </Button>
              )}
            </div>
            {paymentDefinitionsOpen && (
              <>
                {hasSelectedZidChannels && paymentStoreMethods.length === 0 && !fieldsDisabled && (
                  <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.channelMethods.emptyPayment')}</div>
                )}
                {hasMissingShippingScope && !fieldsDisabled && (
                  <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--warning-100)', color: 'var(--warning)', fontSize: 12.5, lineHeight: 1.45 }}>{t('pages.syncRuleWizard.channelMethods.missingShippingScope')}</div>
                )}
                <div style={{ display: 'grid', gap: 14, marginTop: 16, minWidth: 0 }}>
                  {renderPaymentCriteriaEditor(paymentDraft, patch => setPaymentDraft(current => ({ ...current, ...patch })), addPaymentDraftCriteriaField, removePaymentDraftCriteriaField)}
                  <div style={{ display: 'grid', gap: 3, padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)' }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.mappingSections.zohoResultTitle')}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.mappingSections.paymentResultBody')}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 10, alignItems: 'end', minWidth: 0 }}>
                    <SelectField label={t('pages.syncRuleWizard.fields.zohoPaymentTerms')} value={paymentDraft.paymentTermsExternalId} onChange={value => setPaymentDraft(current => ({ ...current, paymentTermsExternalId: value }))} items={paymentTerms} placeholder={t('pages.syncRuleWizard.placeholders.selectRequired')} disabled={fieldsDisabled} />
                    {renderServiceProductField({
                      label: t('pages.syncRuleWizard.fields.paymentFeeServiceItem'),
                      helper: t('pages.syncRuleWizard.serviceItems.paymentHelper'),
                      selectedId: paymentDraft.paymentFeeServiceItemProductId,
                      selectedLabel: paymentDraft.paymentFeeServiceItemLabel,
                      onSelect: (productId, label) => setPaymentDraft(current => ({ ...current, paymentFeeServiceItemProductId: productId, paymentFeeServiceItemLabel: label })),
                    })}
                    <Button kind="secondary" size="sm" onClick={addPaymentMapping} disabled={fieldsDisabled || !paymentDraft.paymentTermsExternalId || !hasPaymentMappingCriteria(paymentDraft)} style={mobileFullWidthButtonStyle}>{t('pages.syncRuleWizard.actions.add')}</Button>
                  </div>
                </div>
                {paymentDraftNeedsCriteria && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.paymentMappingNeedsCriteria')}</div>}
                <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                  {paymentMappings.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.paymentMappings.empty')}</div> : paymentMappings.map((mapping, index) => (
                    <div key={`${mapping.paymentTermsExternalId}-${index}`} style={{ display: 'grid', gap: 12, minWidth: 0, padding: '12px', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--ink-50)', fontSize: 13 }}>
                      {renderPaymentCriteriaEditor(mapping, patch => updatePaymentMapping(index, patch), field => addPaymentMappingCriteriaField(index, field), field => removePaymentMappingCriteriaField(index, field))}
                      <div style={{ display: 'grid', gap: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.mappingSections.zohoResultTitle')}</span>
                        <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.mappingSections.paymentResultBody')}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 10, alignItems: 'end', minWidth: 0 }}>
                        <SelectField label={t('pages.syncRuleWizard.fields.zohoPaymentTerms')} value={mapping.paymentTermsExternalId} onChange={value => updatePaymentMapping(index, { paymentTermsExternalId: value })} items={paymentTerms} placeholder={t('pages.syncRuleWizard.placeholders.selectRequired')} disabled={fieldsDisabled} />
                        {renderServiceProductField({
                          label: t('pages.syncRuleWizard.fields.paymentFeeServiceItem'),
                          helper: t('pages.syncRuleWizard.serviceItems.paymentHelper'),
                          selectedId: mapping.paymentFeeServiceItemProductId,
                          selectedLabel: mapping.paymentFeeServiceItemLabel,
                          onSelect: (productId, label) => updatePaymentMapping(index, { paymentFeeServiceItemProductId: productId, paymentFeeServiceItemLabel: label }),
                        })}
                        {!fieldsDisabled && <button aria-label={t('pages.syncRuleWizard.actions.removePaymentMapping')} onClick={() => removePaymentMapping(index)} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800, padding: '9px 0' }}>{t('pages.syncRuleWizard.actions.remove')}</button>}
                      </div>
                    </div>
                  ))}
                </div>
                {hasInvalidPaymentMappings && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.paymentMappingsInvalid')}</div>}
              </>
            )}
          </Card>

          <Card padding={24}>
            <button type="button" aria-expanded={shippingDefinitionsOpen} onClick={() => setShippingDefinitionsOpen(open => !open)} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 10, alignItems: 'start', background: 'transparent', border: 'none', padding: 0, textAlign: 'start', cursor: 'pointer', fontFamily: 'inherit', minWidth: 0 }}>
              <Icon size={16} color="var(--fg-muted)" style={{ transform: shippingDefinitionsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform var(--dur-fast) var(--ease-out)', marginTop: 2 }}>{I.chevronD}</Icon>
              <span style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.shippingMappings.title')}</span>
                  <Pill tone="neutral" size="sm">{t('pages.syncRuleWizard.mappingSections.definitionsCount', { count: shippingMappings.length })}</Pill>
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.shippingMappings.body')}</span>
              </span>
            </button>
            {shippingDefinitionsOpen && (
              <>
                {hasSelectedZidChannels && shippingStoreMethods.length === 0 && !fieldsDisabled && (
                  <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.channelMethods.emptyShipping')}</div>
                )}
                <div style={{ display: 'grid', gap: 14, marginTop: 16, minWidth: 0 }}>
                  {renderShippingCriteriaEditor(shippingDraft, patch => setShippingDraft(current => ({ ...current, ...patch })), addShippingDraftCriteriaField, removeShippingDraftCriteriaField)}
                  <div style={{ display: 'grid', gap: 3, padding: '10px 12px', borderRadius: 10, background: 'var(--ink-50)' }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.mappingSections.zohoResultTitle')}</span>
                    <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.mappingSections.shippingResultBody')}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 10, alignItems: 'end', minWidth: 0 }}>
                    <SelectField label={t('pages.syncRuleWizard.fields.zohoDeliveryMethod')} value={shippingDraft.deliveryMethodExternalId} onChange={value => setShippingDraft(current => ({ ...current, deliveryMethodExternalId: value }))} items={deliveryMethods} placeholder={t('pages.syncRuleWizard.placeholders.selectRequired')} disabled={fieldsDisabled} />
                    {renderServiceProductField({
                      label: t('pages.syncRuleWizard.fields.shippingFeeServiceItem'),
                      helper: t('pages.syncRuleWizard.serviceItems.shippingHelper'),
                      selectedId: shippingDraft.shippingFeeServiceItemProductId,
                      selectedLabel: shippingDraft.shippingFeeServiceItemLabel,
                      onSelect: (productId, label) => setShippingDraft(current => ({ ...current, shippingFeeServiceItemProductId: productId, shippingFeeServiceItemLabel: label })),
                    })}
                    <Button kind="secondary" size="sm" onClick={addShippingMapping} disabled={fieldsDisabled || !shippingDraft.deliveryMethodExternalId || !hasShippingMappingCriteria(shippingDraft)} style={mobileFullWidthButtonStyle}>{t('pages.syncRuleWizard.actions.add')}</Button>
                  </div>
                </div>
                {shippingDraftNeedsCriteria && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.shippingMappingNeedsCriteria')}</div>}
                <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
                  {shippingMappings.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.shippingMappings.empty')}</div> : shippingMappings.map((mapping, index) => (
                    <div key={`${mapping.deliveryMethodExternalId}-${index}`} style={{ display: 'grid', gap: 12, minWidth: 0, padding: '12px', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--ink-50)', fontSize: 13 }}>
                      {renderShippingCriteriaEditor(mapping, patch => updateShippingMapping(index, patch), field => addShippingMappingCriteriaField(index, field), field => removeShippingMappingCriteriaField(index, field))}
                      <div style={{ display: 'grid', gap: 3 }}>
                        <span style={{ fontSize: 12, fontWeight: 900, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.mappingSections.zohoResultTitle')}</span>
                        <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.mappingSections.shippingResultBody')}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'minmax(0, 1fr) minmax(0, 1fr) auto', gap: 10, alignItems: 'end', minWidth: 0 }}>
                        <SelectField label={t('pages.syncRuleWizard.fields.zohoDeliveryMethod')} value={mapping.deliveryMethodExternalId} onChange={value => updateShippingMapping(index, { deliveryMethodExternalId: value })} items={deliveryMethods} placeholder={t('pages.syncRuleWizard.placeholders.selectRequired')} disabled={fieldsDisabled} />
                        {renderServiceProductField({
                          label: t('pages.syncRuleWizard.fields.shippingFeeServiceItem'),
                          helper: t('pages.syncRuleWizard.serviceItems.shippingHelper'),
                          selectedId: mapping.shippingFeeServiceItemProductId,
                          selectedLabel: mapping.shippingFeeServiceItemLabel,
                          onSelect: (productId, label) => updateShippingMapping(index, { shippingFeeServiceItemProductId: productId, shippingFeeServiceItemLabel: label }),
                        })}
                        {!fieldsDisabled && <button aria-label={t('pages.syncRuleWizard.actions.removeShippingMapping')} onClick={() => removeShippingMapping(index)} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800, padding: '9px 0' }}>{t('pages.syncRuleWizard.actions.remove')}</button>}
                      </div>
                    </div>
                  ))}
                </div>
                {hasInvalidShippingMappings && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.shippingMappingsInvalid')}</div>}
              </>
            )}
          </Card>

          <Card padding={24}>
            <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.warehouseOverrides.title')}</h3>
            <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', margin: '6px 0 18px', lineHeight: 1.55 }}>{t('pages.syncRuleWizard.warehouseOverrides.body')}</p>
            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? singleColumnGrid : 'repeat(4, minmax(0, 1fr))', gap: 10, minWidth: 0 }}>
                <TextInput label={t('pages.syncRuleWizard.fields.rulePriority')} value={warehouseOverrideDraft.priority} onChange={value => updateWarehouseOverrideDraft({ priority: value })} placeholder={t('pages.syncRuleWizard.placeholders.priority')} disabled={fieldsDisabled} />
                <OptionSelect label={t('pages.syncRuleWizard.fields.channelConnection')} value={warehouseOverrideDraft.channelConnectionId} onChange={value => {
                  const selected = channelConnections.find(connection => String(connection.id) === value)
                  updateWarehouseOverrideDraft({ channelConnectionId: value, channelProvider: selected?.provider ?? '' })
                }} options={channelOptions} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
                <OptionSelect label={t('pages.syncRuleWizard.fields.orderStatus')} value={warehouseOverrideDraft.orderStatus} onChange={value => updateWarehouseOverrideDraft({ orderStatus: value })} options={orderStatusOptions} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.rawStatus')} value={warehouseOverrideDraft.rawStatus} onChange={value => updateWarehouseOverrideDraft({ rawStatus: value })} placeholder={t('pages.syncRuleWizard.placeholders.rawStatus')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.paymentStatus')} value={warehouseOverrideDraft.paymentStatus} onChange={value => updateWarehouseOverrideDraft({ paymentStatus: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentStatus')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.paymentMethodCode')} value={warehouseOverrideDraft.paymentMethodCode} onChange={value => updateWarehouseOverrideDraft({ paymentMethodCode: value })} placeholder={t('pages.syncRuleWizard.placeholders.paymentMethodCode')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.shippingCompany')} value={warehouseOverrideDraft.shippingCompany} onChange={value => updateWarehouseOverrideDraft({ shippingCompany: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingCompany')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.shippingMethod')} value={warehouseOverrideDraft.shippingMethod} onChange={value => updateWarehouseOverrideDraft({ shippingMethod: value })} placeholder={t('pages.syncRuleWizard.placeholders.shippingMethod')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.fulfillmentChannel')} value={warehouseOverrideDraft.fulfillmentChannel} onChange={value => updateWarehouseOverrideDraft({ fulfillmentChannel: value })} placeholder={t('pages.syncRuleWizard.placeholders.fulfillmentChannel')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.fulfillmentStatus')} value={warehouseOverrideDraft.fulfillmentStatus} onChange={value => updateWarehouseOverrideDraft({ fulfillmentStatus: value })} placeholder={t('pages.syncRuleWizard.placeholders.fulfillmentStatus')} disabled={fieldsDisabled} />
                <Button kind="secondary" size="sm" onClick={applyFbnPreset} disabled={fieldsDisabled} style={mobileFullWidthButtonStyle}>{t('pages.syncRuleWizard.actions.fbnPreset')}</Button>
                <Button kind="secondary" size="sm" onClick={applyFlexPreset} disabled={fieldsDisabled} style={mobileFullWidthButtonStyle}>{t('pages.syncRuleWizard.actions.flexPreset')}</Button>
                <OptionSelect label={t('pages.syncRuleWizard.fields.skuMatchScope')} value={warehouseOverrideDraft.skuMatchScope} onChange={value => updateWarehouseOverrideDraft({ skuMatchScope: value as ZohoOrderPolicySkuMatchScope | '' })} options={skuScopeOptions} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
                <OptionSelect label={t('pages.syncRuleWizard.fields.skuMatchOperator')} value={warehouseOverrideDraft.skuMatchOperator} onChange={value => updateWarehouseOverrideDraft({ skuMatchOperator: value as ZohoOrderPolicySkuMatchOperator | '' })} options={skuOperatorOptions} placeholder={t('pages.syncRuleWizard.placeholders.selectOptional')} disabled={fieldsDisabled} />
                <TextInput label={t('pages.syncRuleWizard.fields.skuMatchValue')} value={warehouseOverrideDraft.skuMatchValue} onChange={value => updateWarehouseOverrideDraft({ skuMatchValue: value })} placeholder={t('pages.syncRuleWizard.placeholders.skuPrefix')} disabled={fieldsDisabled} />
                <SelectField label={t('pages.syncRuleWizard.fields.targetWarehouse')} value={warehouseOverrideDraft.warehouseExternalId} onChange={value => updateWarehouseOverrideDraft({ warehouseExternalId: value })} items={activeItems('WAREHOUSE')} placeholder={t('pages.syncRuleWizard.placeholders.selectRequired')} disabled={fieldsDisabled} />
              </div>
              <Button kind="secondary" size="sm" onClick={addWarehouseOverride} disabled={fieldsDisabled || !hasWarehouseOverrideCondition(warehouseOverrideDraft) || !warehouseOverrideDraft.warehouseExternalId} style={mobileFullWidthButtonStyle}>{t('pages.syncRuleWizard.actions.addWarehouseOverride')}</Button>
            </div>
            <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
              {warehouseOverrides.length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{t('pages.syncRuleWizard.warehouseOverrides.empty')}</div> : warehouseOverrides.map((rule, index) => (
                <div key={`${rule.priority}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: isMobile ? 'wrap' : undefined, minWidth: 0, padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--ink-50)', fontSize: 13 }}>
                  <span style={{ minWidth: 0, overflowWrap: 'break-word' }}>{t('pages.syncRuleWizard.warehouseOverrides.summary', { priority: rule.priority, status: rule.orderStatus || rule.rawStatus || t('pages.syncRuleWizard.emptyValue'), warehouse: activeItems('WAREHOUSE').find(item => item.externalId === rule.warehouseExternalId)?.displayName ?? rule.warehouseExternalId })}</span>
                  {!fieldsDisabled && <button onClick={() => removeWarehouseOverride(index)} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 800 }}>{t('pages.syncRuleWizard.actions.remove')}</button>}
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div style={{ display: 'grid', gap: 16, position: isMobile ? 'static' : 'sticky', top: isMobile ? undefined : 24, minWidth: 0 }}>
          <Card padding={20}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Icon size={17} color="var(--brand-700)">{I.shield}</Icon>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.active.title')}</h3>
            </div>
            {activePolicy ? (
              <div>
                <Pill tone="success" dot>{t(`pages.syncRuleWizard.status.${activePolicy.status}`)}</Pill>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--fg)', marginTop: 12 }}>{activePolicy.name}</div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', marginTop: 5, lineHeight: 1.5 }}>{activeSummary}</div>
              </div>
            ) : (
              <div style={{ border: '1px dashed var(--border-default)', borderRadius: 12, padding: 14, background: 'var(--ink-50)' }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--brand-900)' }}>{t('pages.syncRuleWizard.active.emptyTitle')}</div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5, marginTop: 4 }}>{t('pages.syncRuleWizard.active.emptyBody')}</div>
              </div>
            )}
          </Card>

          {selectedPolicy && (
            <Card padding={20}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.autoSend.title')}</h3>
                  <div style={{ fontSize: 12.5, color: selectedAutoSendEnabled ? 'var(--success)' : 'var(--fg-muted)', marginTop: 5, fontWeight: 700 }}>
                    {selectedAutoSendEnabled ? t('pages.syncRuleWizard.autoSend.enabled') : t('pages.syncRuleWizard.autoSend.disabled')}
                  </div>
                </div>
                <Toggle
                  on={selectedAutoSendEnabled}
                  onChange={handleToggleAutoSend}
                  disabled={selectedPolicy.status !== 'ACTIVE' || togglingAutoSend}
                  ariaLabel={t('pages.syncRuleWizard.autoSend.toggleLabel', { name: selectedPolicy.name })}
                />
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5, margin: '10px 0 0' }}>
                {t(selectedPolicy.status === 'ACTIVE' ? 'pages.syncRuleWizard.autoSend.help' : 'pages.syncRuleWizard.autoSend.activeOnlyHelp')}
              </p>
              {togglingAutoSend && <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 8 }}>{t('pages.syncRuleWizard.autoSend.saving')}</div>}
            </Card>
          )}

          <Card padding={20}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>
              {isViewingLockedPolicy ? t('pages.syncRuleWizard.draft.viewTitle') : isEditingDraft ? t('pages.syncRuleWizard.draft.editTitle') : t('pages.syncRuleWizard.draft.createTitle')}
            </h3>
            <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5, margin: '10px 0 0' }}>
              {isViewingLockedPolicy && selectedPolicy
                ? t('pages.syncRuleWizard.draft.viewBody', { status: t(`pages.syncRuleWizard.status.${selectedPolicy.status}`), version: selectedPolicy.version })
                : isEditingDraft && selectedPolicy
                  ? t('pages.syncRuleWizard.draft.editBody', { version: selectedPolicy.version })
                  : t('pages.syncRuleWizard.draft.createBody')}
            </p>
            {!hasRequiredChannels && !fieldsDisabled && <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.channelsRequired')}</div>}
            {!hasRequiredActionMappings && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.actionsRequired')}</div>}
            {hasInvalidPaymentMappings && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.paymentMappingsInvalid')}</div>}
            {hasInvalidShippingMappings && !fieldsDisabled && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--warning)', lineHeight: 1.45 }}>{t('pages.syncRuleWizard.validation.shippingMappingsInvalid')}</div>}
            <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
              {!isViewingLockedPolicy && <Button kind="primary" size="sm" onClick={handleSaveDraft} disabled={!canSaveDraft}>{saving ? t('pages.syncRuleWizard.actions.savingDraft') : isEditingDraft ? t('pages.syncRuleWizard.actions.saveDraft') : t('pages.syncRuleWizard.actions.createDraft')}</Button>}
              <Button kind="ghost" size="sm" onClick={onBack}>{t('pages.syncRuleWizard.actions.backToZoho')}</Button>
            </div>
          </Card>

          <Card padding={20}>
            <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)', margin: 0 }}>{t('pages.syncRuleWizard.deferred.title')}</h3>
            <p style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.5, margin: '8px 0 0' }}>{t('pages.syncRuleWizard.deferred.body')}</p>
          </Card>
        </div>
      </div>
    </div>
  )
}
